import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';

/**
 * NT-02 — the in-app notification centre, in the top bar beside the profile.
 *
 * The API has existed since notifications shipped (`GET /notifications/mine`, `GET
 * /shop/notifications`, and a PATCH to mark one read) and **nothing in the web called it**, so
 * every in-app notification the portal recorded was written to a table nobody could see. A-24 is
 * the Admin screen for channel switches and the delivery log; it is not where a person reads
 * their own messages, and it is staff-only besides — a retailer had no way to see one at all.
 *
 * One component for both shells. The endpoints differ because the audiences do — a retailer's
 * notifications are scoped to their retailer user, staff to their internal user — and that is
 * the only difference, so passing the base path keeps one bell rather than two that drift.
 *
 * **Polled, not pushed.** There is no websocket on this project and adding one for a bell would
 * be a lot of moving parts for a number. Sixty seconds is slow enough to be invisible in load
 * and fast enough that a notification raised while you are looking at a screen arrives before
 * you have finished reading it.
 */

export interface InAppNotification {
  id: string;
  title: string;
  body: string;
  link: string | null;
  type: string;
  createdAt: string;
  readAt: string | null;
}

export function NotificationBell({ base }: { base: 'shop' | 'internal' }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  const listPath = base === 'shop' ? '/shop/notifications' : '/notifications/mine';
  const readPath = (id: string) => (base === 'shop' ? `/shop/notifications/${id}/read` : `/notifications/${id}/read`);
  const key = ['notifications', base];

  const { data } = useQuery({
    queryKey: key,
    queryFn: () => api.get<InAppNotification[]>(listPath),
    refetchInterval: 60_000,
    /*
     * A failure here must not break the page the person is actually using. The bell is chrome:
     * if the request fails it renders as "nothing unread", which is wrong but harmless, where a
     * thrown error inside the shell would take the whole screen down.
     */
    retry: false,
  });

  const items = data ?? [];
  const unread = items.filter((n) => n.readAt === null);

  // Close on a click outside, and on Escape — a panel you cannot dismiss is a trap.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  async function markRead(n: InAppNotification): Promise<void> {
    if (n.readAt !== null) return;
    try {
      await api.patch(readPath(n.id), {});
      await qc.invalidateQueries({ queryKey: key });
    } catch {
      /* Reading is a convenience; a failure here must not stop the link being followed. */
    }
  }

  return (
    <div className="mk-bell-wrap" ref={box}>
      <button
        type="button"
        className="mk-bell"
        aria-label={unread.length > 0 ? t('bell.unread', { n: unread.length }) : t('bell.none')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {/* Inline, so the bell needs no icon set and no network request of its own. */}
        <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden focusable="false">
          <path
            d="M10 2a5 5 0 0 0-5 5v3.2L3.6 13a.6.6 0 0 0 .5.9h11.8a.6.6 0 0 0 .5-.9L15 10.2V7a5 5 0 0 0-5-5Zm0 15a2.2 2.2 0 0 0 2.1-1.6H7.9A2.2 2.2 0 0 0 10 17Z"
            fill="currentColor"
          />
        </svg>
        {unread.length > 0 ? <span className="mk-bell-dot">{unread.length > 9 ? '9+' : unread.length}</span> : null}
      </button>

      {open ? (
        <div className="mk-bell-panel" role="dialog" aria-label={t('bell.title')}>
          <div className="mk-bell-head">{t('bell.title')}</div>
          {items.length === 0 ? (
            <p className="mk-bell-empty">{t('bell.empty')}</p>
          ) : (
            <ul className="mk-bell-list">
              {items.slice(0, 12).map((n) => {
                const row = (
                  <>
                    <span className="mk-bell-t">{n.title}</span>
                    <span className="mk-bell-b">{n.body}</span>
                  </>
                );
                return (
                  <li key={n.id} className={n.readAt === null ? 'mk-bell-i mk-unread' : 'mk-bell-i'}>
                    {/* NT-02 — "links to the record". A notification with nowhere to go is read in place. */}
                    {n.link ? (
                      <Link to={n.link} onClick={() => { setOpen(false); void markRead(n); }}>{row}</Link>
                    ) : (
                      <button type="button" onClick={() => void markRead(n)}>{row}</button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
